/**
 * Locale-aware formatting utilities using `Intl` APIs.
 *
 * All functions accept a locale string (e.g. `"ar"`, `"en"`) and
 * automatically handle RTL number formatting for Arabic.
 */

/**
 * Parse a numeric string that may contain locale-formatted thousand separators
 * (e.g. "1,000.00" → 1000). Safe for both plain strings and numbers.
 */
export function parseApiFloat(value: string | number | null | undefined): number {
    if (value === null || value === undefined) return 0;
    if (typeof value === "number") return value;
    return parseFloat(value.replace(/,/g, ""));
}

/**
 * Format a number as NC points.
 *
 * @param amount - The numeric amount to format (can be string or number)
 * @param locale - BCP-47 locale string, e.g. `"ar"` or `"en"`
 * @returns Formatted NC string, e.g. `"NC 30.00"`
 *
 * @example
 * ```ts
 * formatCurrency(30, "en");  // "NC 30.00"
 * formatCurrency("30.00", "ar");  // "NC ٣٠٫٠٠"
 * formatCurrency(-370.5, "en"); // "NC -370.50"
 * ```
 */
export function formatCurrency(
    amount: number | string | null | undefined,
    locale: string,
): string {
    const numericAmount = parseApiFloat(amount);

    const formatted = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(numericAmount);

    return `NC ${formatted}`;
}

/**
 * Format a number with locale-appropriate separators.
 *
 * @param value  - The numeric value to format (can be string or number)
 * @param locale - BCP-47 locale string
 * @param options - Additional Intl.NumberFormatOptions
 * @returns Formatted number string
 *
 * @example
 * ```ts
 * formatNumber(1234.5, "en"); // "1,234.5"
 * formatNumber("1234.5", "ar"); // "١٬٢٣٤٫٥"
 * formatNumber(0.234, "en", { style: 'percent' }); // "23.4%"
 * ```
 */
export function formatNumber(
    value: number | string,
    locale: string,
    options?: Intl.NumberFormatOptions
): string {
    const numericValue = parseApiFloat(value);

    return new Intl.NumberFormat(locale, options).format(numericValue);
}

/**
 * Format an ISO 8601 date string into a human-readable format.
 *
 * @param dateStr - ISO 8601 date string (e.g. `"2024-02-01T10:30:00Z"`)
 * @param locale  - BCP-47 locale string
 * @param style   - `"short"` for numeric dates, `"long"` for text dates
 * @returns Formatted date string
 *
 * @example
 * ```ts
 * formatDate("2024-02-01T10:30:00Z", "en", "short"); // "2/1/2024"
 * formatDate("2024-02-01T10:30:00Z", "ar", "long");  // "١ فبراير ٢٠٢٤"
 * ```
 */
export function formatDate(
    dateStr: string,
    locale: string,
    style: "short" | "long" = "short"
): string {
    const date = new Date(dateStr);

    if (style === "long") {
        return new Intl.DateTimeFormat(locale, {
            year: "numeric",
            month: "long",
            day: "numeric",
        }).format(date);
    }

    return new Intl.DateTimeFormat(locale, {
        year: "numeric",
        month: "numeric",
        day: "numeric",
    }).format(date);
}

/**
 * Format a relative time description (e.g. "3 days ago").
 *
 * @param dateStr - ISO 8601 date string
 * @param locale  - BCP-47 locale string
 * @returns Relative time string
 *
 * @example
 * ```ts
 * formatRelativeTime("2024-02-01T10:30:00Z", "en"); // "2 months ago"
 * ```
 */
export function formatRelativeTime(dateStr: string, locale: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

    if (diffDays === 0) return rtf.format(0, "day");
    if (diffDays < 7) return rtf.format(-diffDays, "day");
    if (diffDays < 30) return rtf.format(-Math.floor(diffDays / 7), "week");
    if (diffDays < 365) return rtf.format(-Math.floor(diffDays / 30), "month");
    return rtf.format(-Math.floor(diffDays / 365), "year");
}

/**
 * Format a percentage value with locale-appropriate formatting.
 *
 * @param value - The numeric value (0-100) or decimal (0-1)
 * @param locale - BCP-47 locale string
 * @param fractionDigits - Number of decimal places (default: 1)
 * @returns Formatted percentage string
 *
 * @example
 * ```ts
 * formatPercent(23.4, "en"); // "23.4%"
 * formatPercent(0.234, "en"); // "0.2%"
 * formatPercent(100, "ar"); // "١٠٠٪"
 * ```
 */
export function formatPercent(
    value: number | string,
    locale: string,
    fractionDigits = 1
): string {
    const numericValue = parseApiFloat(value);

    return new Intl.NumberFormat(locale, {
        style: "percent",
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    }).format(numericValue / 100);
}

/**
 * Format a number in compact notation (e.g., 1.2K, 3.4M)
 *
 * @param value - The numeric value to format
 * @param locale - BCP-47 locale string
 * @returns Formatted compact number string
 *
 * @example
 * ```ts
 * formatCompactNumber(1234, "en"); // "1.2K"
 * formatCompactNumber(1234567, "en"); // "1.2M"
 * formatCompactNumber(1234, "ar"); // "١٫٢ ألف"
 * ```
 */
export function formatCompactNumber(value: number | string, locale: string): string {
    const numericValue = parseApiFloat(value);

    return new Intl.NumberFormat(locale, {
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(numericValue);
}

/**
 * Format points/balance value (string from API) as a localized number
 * Handles API string format like "30.00" and formats with locale
 *
 * @param value - String value from API (e.g., "30.00", "200.00")
 * @param locale - BCP-47 locale string
 * @returns Formatted number string without decimal places if .00
 *
 * @example
 * ```ts
 * formatPoints("30.00", "en"); // "30"
 * formatPoints("200.50", "en"); // "200.5"
 * formatPoints("300.00", "ar"); // "٣٠٠"
 * ```
 */
export function formatPoints(value: string, locale: string): string {
    const numericValue = parseApiFloat(value);

    // If value is a whole number (ends in .00), show without decimals
    if (numericValue === Math.floor(numericValue)) {
        return new Intl.NumberFormat(locale, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(numericValue);
    }

    // Otherwise, show with decimals
    return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(numericValue);
}
